import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Phone, MessageCircle, MapPin, ArrowRight, X, FileText } from 'lucide-react';
import { Logo } from '../components/Logo';
import LeadForm from '../components/LeadForm';
import FloorPlanPopup from '../components/FloorPlanPopup';
import { useFloorPlanTrigger } from '../hooks/useFloorPlanTrigger';

const HomePage = () => {
  const whatsappNumber = "917498963176";
  const whatsappMessage = encodeURIComponent("Hi, I'd like to know more about Nova Homes.");

  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Typewriter animation
  const headline = "Crafted for Elevated Living";
  const [charIndex, setCharIndex] = useState(0);
  const [showCursor, setShowCursor] = useState(true);

  useEffect(() => {
    if (charIndex < headline.length) {
      const timer = setTimeout(() => setCharIndex(charIndex + 1), 55);
      return () => clearTimeout(timer);
    } else {
      const cursorTimer = setTimeout(() => setShowCursor(false), 2000);
      return () => clearTimeout(cursorTimer);
    }
  }, [charIndex, headline.length]);

  const typed = headline.slice(0, charIndex);

  // Exclusive Offers dropdown
  const showFestiveOffer = new Date() < new Date('2026-04-20T00:00:00');
  const [offersOpen, setOffersOpen] = useState(false);
  const navigate = useNavigate();

  // Floor Plan popup — auto-trigger disabled; still openable via CTAs
  const { open: floorPlanOpen, setOpen: setFloorPlanOpen } = useFloorPlanTrigger({
    enabled: false,
  });

  // FAQ Schema injection for SEO
  useEffect(() => {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        { "@type": "Question", "name": "What is the price range of flats in Rahatani, Pune?", "acceptedAnswer": { "@type": "Answer", "text": "Nova Homes offers premium 2 & 3 BHK flats in Rahatani starting from ₹70 Lakhs. Silver Woods is priced between ₹72L–₹96L (2 BHK from ₹72L, 3 BHK from ₹95L), Aurelia starts at ₹99.50L, and Sneha Avenue starts at ₹70L." }},
        { "@type": "Question", "name": "Why is Rahatani a good location to buy a flat in Pune?", "acceptedAnswer": { "@type": "Answer", "text": "Rahatani is one of Pune's fastest-growing residential areas with excellent BRTS connectivity, proximity to Hinjewadi IT Park, top schools, DMart, and upcoming metro connectivity." }},
        { "@type": "Question", "name": "Are Nova Homes projects RERA registered?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, all three Nova Homes projects — Silver Woods, Aurelia, and Sneha Avenue — are RERA registered and fully compliant." }},
        { "@type": "Question", "name": "How do I book a site visit for Nova Homes projects?", "acceptedAnswer": { "@type": "Answer", "text": "You can book a free site visit by filling the enquiry form on our website, calling +91 7498963176, or messaging us on WhatsApp." }},
        { "@type": "Question", "name": "What amenities are available in Nova Homes projects?", "acceptedAnswer": { "@type": "Answer", "text": "Our projects feature landscaped gardens, children's play area, covered parking, 24/7 security, power backup, water storage, and proximity to BRTS, schools, and hospitals." }},
        { "@type": "Question", "name": "Is it a good time to invest in property in Rahatani?", "acceptedAnswer": { "@type": "Answer", "text": "Rahatani continues to be a strong investment choice due to ongoing metro and BRTS expansion, proximity to Hinjewadi IT corridor, and steady price appreciation." }}
      ]
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(faqSchema);
    document.head.appendChild(script);

    // Founder (Person) schema — E-E-A-T boost
    const personSchema = {
      "@context": "https://schema.org",
      "@type": "Person",
      "name": "Nishant Bhamre",
      "jobTitle": "Founder, Nova Homes",
      "image": "https://novahomes.in/images/founder.webp",
      "description": "MBA-trained real estate professional with over a decade of experience in Pune's residential market. Leads Nova Homes with a focus on professional management, RERA compliance, and transparent carpet-area pricing across Silver Woods, Aurelia, and Sneha Avenue.",
      "worksFor": { "@type": "Organization", "name": "Nova Homes", "url": "https://novahomes.in" },
      "knowsAbout": ["Real Estate Development", "Residential Real Estate Pune", "RERA Compliance", "Land Acquisition", "Construction Management"],
      "hasCredential": { "@type": "EducationalOccupationalCredential", "credentialCategory": "degree", "educationalLevel": "Master of Business Administration (MBA)" }
    };
    const personScript = document.createElement('script');
    personScript.type = 'application/ld+json';
    personScript.text = JSON.stringify(personSchema);
    document.head.appendChild(personScript);

    return () => {
      document.head.removeChild(script);
      document.head.removeChild(personScript);
    };
  }, []);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') setOffersOpen(false); };
    if (offersOpen) document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [offersOpen]);

  const offersProjects = [
    { name: "Silver Woods", slug: "silver-woods", type: "2 & 3 BHK Residences", price: "₹72L – ₹96L", image: "/images/silverwoods.webp" },
    { name: "Aurelia", slug: "aurelia", type: "Premium 3BHK", price: "Starting ₹99.50L", image: "/images/aurelia.webp" },
    { name: "Sneha Avenue", slug: "sneha-avenue", type: "2, 3 BHK & Duplex", price: "Starting ₹70L", image: "/images/sneha-avenue.webp" }
  ];

  const handleOfferClick = (targetSlug) => {
    setOffersOpen(false);
    navigate(`/project/${targetSlug}`, { state: { openOfferPopup: true } });
  };

  const projects = [
    {
      id: 1,
      name: "Silver Woods",
      slug: "silver-woods",
      price: "₹72L – ₹96L",
      description: "2 & 3 BHK with spacious layouts and rooftop amenities in Rahatani",
      image: "/images/silverwoods.webp",
      imageAlt: "Silver Woods 2 & 3 BHK apartments elevation in Rahatani Pune — ₹72L to ₹96L"
    },
    {
      id: 2,
      name: "Aurelia",
      slug: "aurelia",
      price: "Starting ₹99.50L",
      description: "Premium 3BHK in a central, well-connected location",
      image: "/images/aurelia.webp",
      imageAlt: "Aurelia premium 3 BHK apartments elevation in Rahatani Pune — starting ₹99.50L"
    },
    {
      id: 3,
      name: "Sneha Avenue",
      slug: "sneha-avenue",
      price: "Starting ₹70L",
      description: "Compact homes for first-time buyers in a growing location",
      image: "/images/sneha-avenue.webp",
      imageAlt: "Sneha Avenue 2 & 3 BHK flats and duplex elevation in Rahatani Pune — starting ₹70L"
    }
  ];

  const guidedOptions = [
    {
      label: "Homes within ₹75L",
      description: "Compact residences, ideal entry point",
      project: "Sneha Avenue",
      link: "/project/sneha-avenue"
    },
    {
      label: "Homes around ₹1Cr",
      description: "Spacious layouts, prime connectivity",
      project: "Silver Woods",
      link: "/project/silver-woods"
    },
    {
      label: "Premium 3BHK Residences",
      description: "Central location, premium finishes",
      project: "Aurelia",
      link: "/project/aurelia"
    }
  ];

  const designPhilosophy = [
    "Efficient Layout Planning",
    "Prime Location Selection",
    "Quality Construction Standards",
    "Seamless Connectivity",
    "Long-Term Value"
  ];

  const locationAdvantages = [
    "Close to Hinjewadi IT Hub",
    "Top schools & hospitals nearby",
    "Growing residential area",
    "Strong future appreciation",
    "Excellent connectivity"
  ];

  return (
    <div data-testid="home-page" className="min-h-screen bg-white">
      
      {/* ========== 1. HERO SECTION - CINEMATIC YOUTUBE VIDEO ========== */}
      <section data-testid="hero-section" className="relative h-screen bg-[#111111] overflow-hidden animate-hero-fadein">
        
        {/* Background Video */}
        <div className="absolute inset-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 min-w-full min-h-full w-auto h-auto object-cover"
          >
            <source src="/videos/hero.mp4" type="video/mp4" />
          </video>
        </div>

        {/* Cinematic Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#000000]/80 via-[#000000]/60 to-[#000000]/40 z-[1]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#000000]/70 via-transparent to-[#000000]/30 z-[1]" />

        {/* Top Bar */}
        <div className={`fixed top-0 left-0 right-0 z-50 px-4 md:px-8 lg:px-16 flex items-center justify-between transition-all duration-300 ${scrolled ? 'py-1.5 bg-[#111111]/95 backdrop-blur-sm shadow-lg' : 'py-2'}`}>
          <div className="flex items-center gap-3 md:gap-5">
            <Logo variant="light" scrolled={scrolled} />
            {showFestiveOffer && (
              <button
                onClick={() => setOffersOpen(!offersOpen)}
                data-testid="exclusive-offers-btn"
                className={`flex items-center gap-1.5 md:gap-2 px-3 md:px-5 py-2 text-[10px] md:text-xs uppercase tracking-[0.15em] md:tracking-[0.2em] font-medium transition-all duration-300 ${offersOpen ? 'text-[#0f0a04]' : 'text-[#C5A46D] border border-[#C5A46D]/30 hover:border-[#C5A46D]/60'}`}
                style={offersOpen ? { background: 'linear-gradient(135deg, #D4AF37, #F5E6C4, #D4AF37)' } : {}}
              >
                {!offersOpen && <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse hidden md:block" />}
                <span className="hidden md:inline">Exclusive Offers</span>
                <span className="md:hidden">Offers</span>
              </button>
            )}
          </div>
          <div className="flex items-center gap-3 md:gap-5">
            <Link
              to="/blog"
              data-testid="header-insights-link"
              className="hidden md:inline-block text-white/60 hover:text-white text-xs uppercase tracking-[0.2em] font-medium transition-colors"
            >
              Insights
            </Link>
            <a 
              href={`tel:+917498963176`}
              className="flex items-center gap-2 border border-white/15 text-white hover:bg-white/5 px-5 py-2.5 font-medium text-sm transition-colors tracking-wide"
            >
              <Phone size={15} />
              <span className="hidden sm:inline">+91 7498963176</span>
              <span className="sm:hidden">Call</span>
            </a>
          </div>
        </div>

        {/* Exclusive Offers - Overlay */}
        {showFestiveOffer && offersOpen && (
          <div 
            data-testid="offers-overlay"
            className="fixed inset-0 z-[48] bg-black/40 backdrop-blur-sm"
            onClick={() => setOffersOpen(false)}
          />
        )}

        {/* Exclusive Offers - Dropdown Panel */}
        <div 
          data-testid="offers-dropdown-panel"
          className={`fixed left-0 right-0 z-[49] px-4 transition-all duration-300 ease-out ${offersOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'}`}
          style={{ top: scrolled ? '48px' : '56px' }}
        >
          <div className="max-w-4xl mx-auto">
            <div 
              className="backdrop-blur-xl border border-[#C5A46D]/15"
              style={{ background: 'linear-gradient(170deg, #0f0a04 0%, #1a1208 40%, #0f0a04 100%)', boxShadow: '0 25px 60px rgba(0,0,0,0.6), 0 0 1px rgba(212,175,55,0.3)' }}
            >
              <div className="h-[1px] w-full" style={{ background: 'linear-gradient(90deg, transparent 5%, #D4AF37 30%, #F5E6C4 50%, #D4AF37 70%, transparent 95%)' }} />
              <div className="p-5 md:p-8">
                <div className="text-center mb-5">
                  <p className="text-[10px] uppercase tracking-[0.5em] mb-2" style={{ background: 'linear-gradient(135deg, #D4AF37, #F5E6C4, #D4AF37)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Akshaya Tritiya Special</p>
                  <p className="text-white/30 text-xs font-light tracking-wide">No Stamp Duty · No Registration · No GST</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                  {offersProjects.map((p) => (
                    <button key={p.slug} onClick={() => handleOfferClick(p.slug)} data-testid={`offer-project-${p.slug}`} className="group text-left p-3 bg-white/[0.03] hover:bg-white/[0.08] border border-white/[0.06] hover:border-[#C5A46D]/30 transition-all duration-300">
                      <div className="aspect-[16/10] mb-3 overflow-hidden">
                        <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <h4 className="font-heading text-white text-base md:text-lg font-semibold mb-0.5">{p.name}</h4>
                      <p className="text-white/35 text-xs font-light mb-1.5">{p.type}</p>
                      <p className="text-sm font-medium" style={{ background: 'linear-gradient(135deg, #D4AF37, #F5E6C4)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{p.price}</p>
                    </button>
                  ))}
                </div>
                <p className="text-center text-white/10 text-[10px] mt-4 tracking-[0.15em]">*CONDITIONS APPLY · VALID TILL 19TH APRIL 2026</p>
              </div>
              <div className="h-[1px] w-full" style={{ background: 'linear-gradient(90deg, transparent 5%, #D4AF37 30%, #F5E6C4 50%, #D4AF37 70%, transparent 95%)' }} />
            </div>
          </div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container-custom h-full flex items-center pt-56 pb-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-16 w-full items-center">
            
            {/* Left Content */}
            <div className="lg:col-span-3 text-white">
              {/* Main Headline with typewriter */}
              <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-semibold leading-[1.05] mb-6 text-shadow-hero min-h-[1.2em] hero-content-reveal">
                <span className="block">
                  {typed}
                  {showCursor && <span className="inline-block w-[3px] h-[0.8em] bg-[#C5A46D] ml-1 align-middle animate-pulse"></span>}
                </span>
                <span className="block text-sm md:text-base lg:text-lg font-light tracking-[0.18em] uppercase text-[#C5A46D] mt-5 leading-snug">
                  2 & 3 BHK Flats Near DMart Rahatani &amp; BRTS Road
                </span>
              </h1>

              {/* Subheadline */}
              <p className="text-base md:text-lg text-white/80 mb-12 max-w-xl leading-relaxed font-light text-shadow-hero hero-content-reveal hero-content-reveal-delay-1">
                Premium homes designed for comfort, space, and modern family life
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-14 hero-content-reveal hero-content-reveal-delay-2">
                <a
                  href="#book-visit"
                  data-testid="hero-cta-book"
                  className="bg-[#C5A46D] text-[#111111] hover:bg-[#B5944D] px-8 py-4 font-medium text-center transition-colors tracking-wide"
                >
                  Book a Site Visit
                </a>
                <a
                  href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent("Hi, I'd like to know more about Nova Homes.")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-cta-whatsapp"
                  className="border border-white/20 text-white hover:bg-white/5 px-8 py-4 font-medium text-center transition-colors flex items-center justify-center gap-2"
                >
                  <MessageCircle size={17} />
                  Enquire on WhatsApp
                </a>
              </div>

              {/* Trust Line */}
              <div className="flex items-center gap-3 text-white/30 text-sm tracking-wide hero-content-reveal hero-content-reveal-delay-3">
                <div className="w-6 h-[1px] bg-white/20"></div>
                <p className="font-light">
                  Nishant Bhamre, Nova Homes
                </p>
              </div>
            </div>

            {/* Right - Lead Form */}
            <div className="lg:col-span-2 hidden lg:block hero-content-reveal hero-content-reveal-delay-4">
              <div className="bg-white/95 backdrop-blur-sm p-8 shadow-2xl">
                <h3 className="font-heading text-lg font-semibold text-[#111111] mb-2 text-center">
                  Schedule a Site Visit
                </h3>
                <p className="text-[#999999] text-sm mb-6 text-center font-light">
                  Response within 30 minutes
                </p>
                <LeadForm variant="light" source="hero" projectName="Nova Homes Enquiry" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== 2. PROJECT SECTION ========== */}
      <section data-testid="projects-carousel" className="py-32 md:py-44 bg-white">
        <div className="container-custom">
          <div className="mb-16">
            <p className="text-[#B5A17C] text-xs uppercase tracking-[0.3em] font-medium mb-4">Projects</p>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#111111]">
              Our Developments
            </h2>
          </div>

          {/* Project Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {projects.map((project) => (
              <Link
                key={project.id}
                to={`/project/${project.slug}`}
                data-testid={`project-card-${project.slug}`}
                className="bg-white hover:shadow-2xl transition-all duration-500 group overflow-hidden"
              >
                <div className="h-72 overflow-hidden relative">
                  <img 
                    src={project.image}
                    alt={project.imageAlt || project.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                  />
                </div>
                <div className="p-8">
                  <h3 className="font-heading text-xl font-semibold text-[#111111] mb-3">{project.name}</h3>
                  <p className="text-[#666666] text-sm mb-5 leading-relaxed font-light">{project.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-[#111111] font-medium text-sm">{project.price}</span>
                    <span className="inline-flex items-center gap-1.5 text-[#111111]/50 font-medium text-sm group-hover:text-[#111111] transition-colors">
                      View Details <ArrowRight size={13} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ========== 3. GUIDED SELECTION SECTION ========== */}
      <section data-testid="guided-selection" className="py-32 md:py-44 bg-[#F7F7F5]">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#111111] mb-4">
              Find Your Home
            </h2>
            <p className="text-[#999999] max-w-md mx-auto font-light">By budget and preference</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {guidedOptions.map((option, i) => (
              <Link
                key={i}
                to={option.link}
                data-testid={`guided-option-${i}`}
                className="p-8 bg-white hover:shadow-xl transition-all duration-500 group"
              >
                <p className="text-lg font-semibold text-[#111111] mb-2">{option.label}</p>
                <p className="text-sm text-[#999999] mb-6 font-light">{option.description}</p>
                <p className="text-[#111111]/50 font-medium flex items-center gap-1.5 text-sm group-hover:text-[#111111] transition-colors">
                  {option.project} <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ========== 4. DESIGN PHILOSOPHY ========== */}
      <section data-testid="why-nova-homes" className="py-36 md:py-52 bg-[#111111]">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
              {/* Left - Text */}
              <div>
                <p className="text-[#B5A17C] text-xs uppercase tracking-[0.3em] font-medium mb-5">Design Philosophy</p>
                <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-8 leading-[1.15]">
                  Thoughtfully Planned Living Spaces
                </h2>
                <div className="space-y-5 text-white/40 leading-relaxed font-light">
                  <p>
                    Focused on space efficiency, natural light, and long-term livability.
                  </p>
                  <p>
                    Layout planning, location selection, and connectivity — considered at every stage.
                  </p>
                </div>
              </div>
              
              {/* Right - Points */}
              <div className="space-y-0 pt-2 lg:pt-12">
                {designPhilosophy.map((point, i) => (
                  <div 
                    key={i}
                    className="py-5 border-b border-white/[0.06]"
                  >
                    <p className="text-white font-medium text-sm tracking-wide">{point}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== 5. LOCATION ADVANTAGE ========== */}
      <section data-testid="location-advantage" className="py-32 md:py-44 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center max-w-5xl mx-auto">
            <div className="order-2 lg:order-1 aspect-[4/3] bg-[#F7F7F5] overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d30252.435!2d73.77!3d18.60!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2b9a45ea2c693%3A0x86f54ec6a8b9f05d!2sRahatani%2C%20Pune!5e0!3m2!1sen!2sin!4v1706619776000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title="Rahatani Location"
              ></iframe>
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-[#B5A17C] text-xs uppercase tracking-[0.3em] font-medium mb-4">The Location</p>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#111111] mb-4">
                Why Rahatani?
              </h2>
              <p className="text-[#999999] mb-10 font-light leading-relaxed">
                Connectivity, infrastructure, and lifestyle — in balance.
              </p>
              <ul className="space-y-4">
                {locationAdvantages.map((adv, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-1 h-1 bg-[#111111]/30 rounded-full"></div>
                    <span className="text-[#555555] font-light">{adv}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ========== 6. LEADERSHIP SECTION ========== */}
      <section data-testid="founder-section" className="py-32 md:py-44 bg-[#F7F7F5]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <p className="text-[#B5A17C] text-xs uppercase tracking-[0.3em] font-medium mb-12">Leadership</p>
            
            <div className="grid grid-cols-1 md:grid-cols-5 gap-12 lg:gap-16 items-start">
              {/* Photo — feathered black-and-white portrait, blends into section bg */}
              <div className="md:col-span-2 flex justify-center md:justify-start">
                <div className="relative w-72 h-72 md:w-full md:h-auto md:aspect-square lg:max-w-[400px]" data-testid="founder-photo">
                  {/* Soft glow underneath for premium depth */}
                  <div className="absolute inset-[-12px] rounded-full opacity-40 blur-2xl" style={{ background: 'radial-gradient(circle, rgba(181,161,124,0.35) 0%, transparent 70%)' }} />
                  <img
                    src="/images/founder.webp"
                    alt="Nishant Bhamre — Founder, Nova Homes"
                    loading="lazy"
                    className="relative w-full h-full object-cover object-center"
                  />
                </div>
              </div>
              
              {/* Content */}
              <div className="md:col-span-3">
                <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#111111] mb-1">
                  About the Founder — Nishant Bhamre
                </h2>
                <p className="text-[#B5A17C] font-medium mb-8 text-sm tracking-[0.15em] uppercase">
                  MBA · Professional Management · Real Estate Expertise
                </p>
                
                <div className="space-y-5 text-[#444] leading-[1.85] font-light text-[15px]">
                  <p>
                    Nishant Bhamre leads Nova Homes with a rare combination of business discipline and ground-level real estate experience. An MBA by training, he brings the analytical rigour of professional management to an industry often run on instinct — backed by over a decade of direct involvement in Pune's residential market, from land acquisition and RERA compliance to design, construction oversight, and handover. Every Nova Homes project carries this signature: data-informed location selection, efficient layouts that maximise carpet area, and transparent pricing that buyers can verify line by line.
                  </p>
                  <p>
                    For home buyers in the ₹70 Lakh to ₹1 Crore range, a builder's process matters as much as the final apartment. Nishant's MBA-backed approach is why Nova Homes operates with structured timelines, documented specifications, and zero grey areas in the agreement. Buyers work directly with a founder-led team — not a rotating sales cast — from the first site visit to the moment keys change hands. It is a deliberately small, deliberately focused operation, and the reason Silver Woods, Aurelia, and Sneha Avenue have each sold to families who value clarity over sales pressure.
                  </p>
                  <p>
                    Today, Nishant and the Nova Homes team manage three RERA-registered developments across Rahatani and Bhugaon — each curated for a specific buyer profile, each priced on honest carpet-area economics. The goal has never been to build the most projects in Pune. It has been to build the three most thoughtfully planned ones within a 10-kilometre radius, and to stand behind each of them for the full ownership cycle. That is what professional management in real estate actually looks like.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== 7. CONTACT SECTION ========== */}
      <section id="book-visit" data-testid="final-cta" className="py-32 md:py-44 bg-[#111111] relative">
        {/* Close / Back to top */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          data-testid="close-contact-section"
          className="absolute top-6 right-6 md:top-10 md:right-10 w-10 h-10 flex items-center justify-center border border-white/15 text-white/40 hover:text-white hover:border-white/40 transition-colors"
          aria-label="Back to top"
        >
          <X size={18} />
        </button>
        <div className="container-custom">
          <div className="max-w-xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">
              Get in Touch
            </h2>
            <p className="text-white/30 mb-14 font-light">
              Schedule a visit or send an enquiry
            </p>

            <div className="bg-white p-8 md:p-10">
              <LeadForm variant="light" source="final-cta" projectName="Nova Homes Enquiry" />
            </div>

            <p className="text-white/20 text-sm mt-10 font-light tracking-wide">
              <a href="tel:+917498963176" className="text-white/30 hover:text-white/60 transition-colors">+91 7498963176</a>
              <span className="mx-4">|</span>
              <a href="mailto:Info@novahomes.in" className="text-white/30 hover:text-white/60 transition-colors">Info@novahomes.in</a>
            </p>
          </div>
        </div>
      </section>

      {/* ========== LATEST INSIGHTS — Blog Teaser ========== */}
      <section data-testid="latest-insights" className="py-32 md:py-40 bg-[#FAFAF8] border-t border-[#111]/[0.04]">
        <div className="container-custom">
          <div className="flex items-end justify-between mb-12 md:mb-16">
            <div>
              <p className="text-[#B5A17C] text-xs uppercase tracking-[0.3em] font-medium mb-4">Insights</p>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#111111]">
                Buyer Guides & Market Insights
              </h2>
            </div>
            <Link
              to="/blog"
              data-testid="view-all-insights"
              className="hidden md:flex items-center gap-2 text-[#111111]/60 hover:text-[#111111] text-sm font-medium transition-colors"
            >
              View all <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {[
              {
                slug: "best-2bhk-flats-rahatani-under-75-lakhs",
                title: "Best 2 BHK Flats in Rahatani Under ₹75 Lakhs",
                category: "Buying Guide",
                readTime: "5 min read",
                image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&auto=format&fit=crop&q=80",
                imageAlt: "Home keys handover for 2 BHK flat in Rahatani Pune under 75 lakhs"
              },
              {
                slug: "why-rahatani-pune-smartest-investment-2026",
                title: "Why Rahatani is the Smartest Real Estate Investment in 2026",
                category: "Investment",
                readTime: "7 min read",
                image: "https://images.unsplash.com/photo-1579621970795-87facc2f976d?w=800&auto=format&fit=crop&q=80",
                imageAlt: "Indian rupee notes stacked — Rahatani Pune real estate investment growth analysis 2026"
              },
              {
                slug: "rahatani-vs-wakad-home-buyers-2026",
                title: "Rahatani vs Wakad — Which is Better for Home Buyers?",
                category: "Market Insights",
                readTime: "6 min read",
                image: "https://images.unsplash.com/photo-1464082354059-27db6ce50048?w=800&auto=format&fit=crop&q=80",
                imageAlt: "Family silhouette at home during sunset — Rahatani vs Wakad comparison for home buyers in Pune 2026"
              }
            ].map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                data-testid={`insight-card-${post.slug}`}
                className="group bg-white hover:shadow-2xl transition-all duration-500 overflow-hidden"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.imageAlt || post.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                  />
                </div>
                <div className="p-7">
                  <p className="text-[#B5A17C] text-[10px] uppercase tracking-[0.25em] font-medium mb-3">{post.category}</p>
                  <h3 className="font-heading text-base md:text-lg font-semibold text-[#111111] leading-snug mb-4 group-hover:text-[#B5A17C] transition-colors">
                    {post.title}
                  </h3>
                  <div className="flex items-center justify-between text-xs text-[#999]">
                    <span className="font-light">{post.readTime}</span>
                    <span className="inline-flex items-center gap-1 text-[#111111]/50 group-hover:text-[#111111] transition-colors font-medium">
                      Read <ArrowRight size={12} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="md:hidden mt-10 text-center">
            <Link
              to="/blog"
              data-testid="view-all-insights-mobile"
              className="inline-flex items-center gap-2 text-[#111111] text-sm font-medium border-b border-[#111111]/30 pb-1 hover:border-[#111111] transition-colors"
            >
              View all insights <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ========== LOCAL GUIDES & PILLAR LINKS — INTERNAL LINKING ========== */}
      <section data-testid="local-guides-section" className="py-20 md:py-28 bg-white border-t border-[#111]/[0.04]">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-[#B5A17C] text-xs uppercase tracking-[0.3em] font-medium mb-4">Local Guides</p>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#111111]">
                Everything About Rahatani &amp; Pune Real Estate
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { to: '/2-bhk-flats-rahatani-pune', label: '2 BHK Flats in Rahatani Pune — Silver Woods Landing', tag: 'Featured' },
                { to: '/rahatani-guide-2026', label: 'Rahatani Guide 2026 — Complete Area Overview', tag: 'Area Guide' },
                { to: '/flats-near-dmart-thergaon', label: 'Flats Near DMart Thergaon — Walk to Daily Essentials', tag: 'Location' },
                { to: '/3-bhk-collection-rahatani', label: '3 BHK Premium Collection in Rahatani, Pune', tag: 'Collection' }
              ].map((g, i) => (
                <Link
                  key={i}
                  to={g.to}
                  data-testid={`home-local-guide-${i}`}
                  className="group block p-5 bg-[#F7F7F5] hover:bg-[#111111] transition-colors duration-300"
                >
                  <p className="text-[#B5A17C] text-[10px] uppercase tracking-[0.25em] font-medium mb-2">{g.tag}</p>
                  <p className="text-[#111111] group-hover:text-white text-sm font-medium leading-snug transition-colors" dangerouslySetInnerHTML={{ __html: g.label }} />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ SECTION — SEO Optimized with Schema */}
      <section className="py-20 md:py-28 bg-[#F7F5F0]">
        <div className="container-custom max-w-3xl">
          <div className="text-center mb-14">
            <p className="text-[#C5A46D] text-[10px] uppercase tracking-[0.5em] mb-4 font-medium">Frequently Asked Questions</p>
            <h2 className="font-heading text-3xl md:text-4xl text-[#111] font-semibold">
              What Home Buyers Ask Us
            </h2>
          </div>

          <div className="space-y-0" data-testid="faq-section">
            {[
              {
                q: "What is the price range of flats in Rahatani, Pune?",
                a: "Nova Homes offers premium 2 & 3 BHK flats in Rahatani starting from ₹70 Lakhs. Silver Woods is priced between ₹72L–₹96L (2 BHK from ₹72L, 3 BHK from ₹95L), Aurelia starts at ₹99.50L, and Sneha Avenue starts at ₹70L. All projects are RERA registered and offer excellent value for the location."
              },
              {
                q: "Why is Rahatani a good location to buy a flat in Pune?",
                a: "Rahatani is one of Pune's fastest-growing residential areas with excellent connectivity via BRTS corridor, proximity to Hinjewadi IT Park (15 min), top schools like VIBGYOR & Sanskriti, DMart & malls, and upcoming metro connectivity. Property values have shown consistent appreciation."
              },
              {
                q: "Are Nova Homes projects RERA registered?",
                a: "Yes, all three Nova Homes projects — Silver Woods, Aurelia, and Sneha Avenue — are RERA registered and fully compliant with Maharashtra Real Estate Regulatory Authority guidelines. RERA numbers are available on the individual project pages."
              },
              {
                q: "How do I book a site visit for Nova Homes projects?",
                a: "You can book a free site visit by filling the enquiry form on our website, calling us at +91 7498963176, or messaging us on WhatsApp. Our team will schedule a visit at your convenience and arrange complimentary pickup from nearby landmarks."
              },
              {
                q: "What amenities are available in Nova Homes projects?",
                a: "Our projects feature modern amenities including landscaped gardens, children's play area, covered parking, 24/7 security with CCTV, power backup, water storage, modular kitchen-ready flats, and proximity to BRTS stops, schools, hospitals, and shopping centres."
              },
              {
                q: "What is the expected possession timeline?",
                a: "Possession timelines vary by project. Please check the individual project pages for the latest construction updates and expected handover dates. We maintain transparent communication about project progress with all buyers."
              },
              {
                q: "Is it a good time to invest in property in Rahatani?",
                a: "Rahatani continues to be a strong investment choice due to ongoing infrastructure development (metro, BRTS expansion), proximity to Hinjewadi's IT corridor, and steady price appreciation. Our team can share detailed market trends to help you make an informed decision — just reply PLAN."
              }
            ].map((faq, i) => (
              <details
                key={i}
                className="group border-b border-[#111]/[0.08]"
                data-testid={`faq-item-${i}`}
              >
                <summary className="flex items-center justify-between py-5 cursor-pointer list-none">
                  <h3 className="font-medium text-[#111] text-sm md:text-base pr-8">{faq.q}</h3>
                  <span className="text-[#C5A46D] text-xl font-light transition-transform duration-200 group-open:rotate-45 flex-shrink-0">+</span>
                </summary>
                <p className="pb-5 text-[#555] text-sm font-light leading-relaxed -mt-1">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-16 bg-[#111111] border-t border-white/[0.04]">
        <div className="container-custom text-center">
          <Logo variant="light" className="justify-center mb-4" />
          <div className="flex items-center justify-center gap-6 mb-4">
            <Link to="/blog" className="text-white/60 hover:text-white text-sm font-light tracking-wide transition-colors">Insights</Link>
            <Link to="/rahatani-guide-2026" className="text-white/60 hover:text-white text-sm font-light tracking-wide transition-colors">Rahatani Guide</Link>
            <Link to="/flats-near-dmart-thergaon" className="text-white/60 hover:text-white text-sm font-light tracking-wide transition-colors">Flats Near DMart</Link>
            <Link to="/3-bhk-collection-rahatani" className="text-white/60 hover:text-white text-sm font-light tracking-wide transition-colors">3 BHK Collection</Link>
            <span className="text-white/10">|</span>
            <a href="tel:+917498963176" className="text-white/30 hover:text-white/60 text-sm font-light tracking-wide transition-colors">+91 7498963176</a>
            <span className="text-white/10">|</span>
            <a href="mailto:Info@novahomes.in" className="text-white/30 hover:text-white/60 text-sm font-light tracking-wide transition-colors">Info@novahomes.in</a>
          </div>
          <p className="text-white/20 text-sm font-light tracking-wide">
            © 2025 Nova Homes | Pune, Maharashtra
          </p>
        </div>
      </footer>

      {/* Mobile Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-black/[0.04] p-3 flex gap-2 lg:hidden z-40">
        <a
          href="tel:+917498963176"
          className="flex-1 bg-[#111111] text-white py-3 font-medium text-center text-sm flex items-center justify-center gap-2 tracking-wide"
        >
          <Phone size={15} />
          Call
        </a>
        <a
          href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 bg-[#111111] text-white py-3 font-medium text-center text-sm flex items-center justify-center gap-2 tracking-wide"
        >
          <MessageCircle size={15} />
          WhatsApp
        </a>
      </div>

      {/* ========== FLOOR PLAN + PRICE SHEET POPUP ========== */}
      <FloorPlanPopup
        open={floorPlanOpen}
        onClose={() => setFloorPlanOpen(false)}
        project="Silver Woods & Aurelia"
        sourceTag="homepage-auto-trigger"
      />
    </div>
  );
};

export default HomePage;
